// Any RFC 9562 UUID (versions 1 to 8), plus the nil and max UUIDs.
const UUID_PATTERN =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/i;

export const isUuid = (value: string): boolean => UUID_PATTERN.test(value);

export const once = <F extends (...args: any[]) => any>(fn: F): F => {
  let result: ReturnType<F> | undefined;
  let called = false;
  return function (this: any, ...args: any[]) {
    if (!called) {
      called = true;
      return (result = fn.apply(this, args));
    }
    return result;
  } as F;
};

export const pick = <T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> => {
  const result: any = {};
  for (const key of keys) {
    result[key] = obj[key];
  }
  return result;
};

const QUICK_REJECT_URLS = [
  "cgi-bin",
  "wp-admin",
  "wp-content",
  "wp-includes",
  ".env",
  "settings.json",
  "config.json",
  ".aws",
];

export const isQuickRejectUrl = (url: URL) => {
  return (
    // A dot-file or dot-directory (.git, .ssh, .firebaserc…), except the standard /.well-known/.
    url.pathname
      .split("/")
      .some((segment) => segment.startsWith(".") && segment !== ".well-known") ||
    url.pathname.endsWith(".php") ||
    url.pathname.endsWith(".php7") ||
    url.pathname.endsWith(".properties") ||
    QUICK_REJECT_URLS.some((part) => url.pathname.includes(part))
  );
};

/**
 * `response` with mutable headers (a fetched or cached response's are immutable), taking over its
 * body: the original must not be read afterwards. It doesn't `clone()` (tee) the body, which would
 * leave a branch unread; workerd's tee then stops pulling a streamed body once that branch's queue
 * is full, and the copy stalls.
 */
export const toMutableResponse = (response: Response): Response =>
  new Response(response.body, response);

export const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/**
 * Parses a URL string, automatically adding https:// protocol if missing.
 * Supports protocol-relative URLs (starting with //) and regular URLs.
 * @param urlString - The URL string to parse
 * @returns A URL object
 * @throws Error if the URL is invalid
 */
export const parseUrl = (urlString: string): URL => {
  if (!urlString || typeof urlString !== "string") {
    throw new Error("Invalid URL: must be a non-empty string");
  }

  const trimmed = urlString.trim();
  if (!trimmed) {
    throw new Error("Invalid URL: cannot be empty");
  }

  // Handle protocol-relative URLs (starting with //)
  if (trimmed.startsWith("//")) {
    return new URL(`https:${trimmed}`);
  }

  // Check if URL already has a protocol
  const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed);

  if (hasProtocol) {
    return new URL(trimmed);
  }

  // Add https:// as default protocol
  return new URL(`https://${trimmed}`);
};
